import { useContext } from 'react'

import {
  Box, Button, Columns, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useTemplatePage from '@hooks/useTemplatePage'

import useAssignTemplatePagePermissionsModal from './hooks/useAssignTemplatePagePermissionsModal'

import Agencies from './components/Agencies'
import BrandThemes from './components/BrandThemes'
import Providers from './components/Providers'
import Publications from './components/Publications'
import ServiceProducts from './components/ServiceProducts'
import StepNavigation from './components/StepNavigation'
import TemplateGroups from './components/TemplateGroups'
import TemplatePageDetails from './components/TemplatePageDetails'

const MODAL_KEY = 'AssignTemplatePagePermissionsModal'

type ModalCallbacks = {
  bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
  createEntityName: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const AssignTemplatePagePermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, bulkAssignTemplatePagePermissions } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const initStepKey = modalPayload?.stepKey
  const initTemplatePageId = modalPayload?.templatePage?.id

  const assignTemplatePagePermissionsPayload = useAssignTemplatePagePermissionsModal({
    activeStepKey: initStepKey,
    templatePageId: initTemplatePageId,
  })

  const {
    activeStepKey,
    callbacks: { preparePermissions, setCurrentStepKey },
    hasNextStep,
    nextStepKey,
    permissions: {
      queued: queuedPermissions,
    },
    saveEnabled,
    templatePage,
  } = assignTemplatePagePermissionsPayload

  const templatePagePayload = useTemplatePage(templatePage)
  const { callbacks: { bulkUpdateTemplatePagePermissions: bulkUpdateFn } } = templatePagePayload

  // To be used with handleCallbackAction
  const bulkAssignTemplatePagePermissionsPayload = {
    callbacks: {
      action: bulkUpdateFn,
      afterAction: closeModal,
      buildActionParams: () => [preparePermissions()],
    },
    toastText: 'Permissions Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} showModal={showModal} size="fullscreen">
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Permissions" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {activeStepKey === 'agencyIds' && (
              <Agencies assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'brandThemeIds' && (
              <BrandThemes assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'providerIds' && (
              <Providers assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'publicationIds' && (
              <Publications assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'serviceProductIds' && (
              <ServiceProducts assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'templateGroupIds' && (
              <TemplateGroups assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <StepNavigation assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />

            {/* @ts-expect-error */}
            <TemplatePageDetails templatePage={templatePage} />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Box marginLeft="auto" width="auto">
          {hasNextStep && (
            <Button
              buttonStyle="primaryEdit"
              disabled={!queuedPermissions.providerIds.length}
              loadingBubbleColor="white"
              // @ts-expect-error TS2322
              onClick={nextStepKey ? () => setCurrentStepKey(nextStepKey) : null}
              size="large"
              width="auto"
            >
              Next Step
            </Button>
          )}

          {saveEnabled && activeStepKey !== 'providerIds' && (
            <Button
              buttonStyle="primaryCreate"
              loadingBubbleColor="white"
              // @ts-expect-error TS2345
              onClick={() => bulkAssignTemplatePagePermissions(bulkAssignTemplatePagePermissionsPayload)}
              marginLeft="medium"
              size="large"
              width="auto"
            >
              Save Permissions
            </Button>
          )}
        </Box>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <AssignTemplatePagePermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
